import { paymentProviderZodSchema } from "@@/shared/payments/payment-providers";
import { priceZodSchema } from "@@/shared/prices/price";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { UUID } from "@@/shared/uuid";
import { asArray } from "@@/utilities/as-array";
import type { Except } from "type-fest";
import { z } from "zod";
import type { _OrderEventBaseFactoryParams } from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

const orderPaymentFulfilledEventZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("PAYMENT_FULFILLED"),
        createdBy: userIdZodSchema.or(z.string()),
        paymentUUID: z.string() as unknown as z.ZodType<UUID>,
        paidAmount: priceZodSchema,
        paymentProvider: paymentProviderZodSchema,
        paymentFor: z.object({
            orderEventId: orderEventIdZodSchema.or(
                z.array(orderEventIdZodSchema),
            ),
        }),
        legacyServiceFee: z.boolean(),
        virtualCardPayment: z.boolean().optional(),
    }),
);
type OrderPaymentFulfilledEvent = z.infer<
    typeof orderPaymentFulfilledEventZodObject
>;
const orderPaymentFulfilledEventZodSchema =
    orderPaymentFulfilledEventZodObject as z.ZodSchema<OrderPaymentFulfilledEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPaymentFulfilledEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;

const orderPaymentFulfilledEventFactory = (
    params: Params,
): OrderPaymentFulfilledEvent => ({
    ..._orderEventBaseFactory({
        ...params,
        signature: [
            params.orderId,
            "PAYMENT_FULFILLED",
            [...asArray(params.paymentFor.orderEventId), params.paymentUUID],
        ],
    }),

    type: "PAYMENT_FULFILLED",
    createdBy: params.createdBy,
    paymentUUID: params.paymentUUID,
    paymentProvider: params.paymentProvider,
    paidAmount: params.paidAmount,
    paymentFor: params.paymentFor,
    legacyServiceFee: params.legacyServiceFee,
});

const isOrderPaymentFulfilledEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is OrderPaymentFulfilledEvent => {
    return item.type === "PAYMENT_FULFILLED";
};

export {
    isOrderPaymentFulfilledEvent,
    orderPaymentFulfilledEventFactory,
    orderPaymentFulfilledEventZodObject,
    orderPaymentFulfilledEventZodSchema,
};
export type { OrderPaymentFulfilledEvent };
